/* eslint-disable no-console */
import {Col, Form, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React from 'react';
import {useSearchParams} from 'react-router-dom';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {CommonErrors} from '../../constants/Common';
import {CADocumentTypeOptions} from '../../pages/aiDocumentManager/Constants/commonConstants';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import {yearSelect} from '../../utils/FormUtils/yearSelect';
import DocumentUploadPreview from '../DocumentUploadPreview/DocumentUploadPreview';

import {
  AIDocumentManagerKey,
  AIDocumentManagerLabel
} from './Constants/IP/AIDocumentManagerLabel';
import {yearKeyLabel} from './Constants/IP/commonKeyLabel';
import {filterSelectOptions} from './Utils/commonFunctions';

const CADocumentManagerForm: React.FC<{
  currentStep?: number;
}> = ({currentStep}) => {
  const FormInstance = useFormInstance();
  const [searchParam] = useSearchParams();

  const yearFilter = yearSelect();

  return (
    <>
      {currentStep === 0 && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={AIDocumentManagerKey.YEAR}
              label={AIDocumentManagerLabel.YEAR}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(AIDocumentManagerLabel.YEAR)
                }
              ]}
              initialValue={searchParam.get('selectedYear')}
            >
              <Select
                placeholder={yearKeyLabel.label}
                options={yearFilter}
                onChange={() => {
                  FormInstance.setFieldsValue({
                    [AIDocumentManagerKey.FACILITY_TYPE]: undefined
                  });
                }}
                suffixIcon={<YearCalender />}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item dependencies={[AIDocumentManagerKey.YEAR]} noStyle>
              {({getFieldValue}) => {
                const selectedYear = getFieldValue(AIDocumentManagerKey.YEAR);

                const facilityDetails =
                  useCompanyStore
                    .getState()
                    .facilityDetails?.[selectedYear]?.map((item: any) => {
                      return {
                        isLeased: item.isLeased,
                        country: item.country,
                        type: item.type,
                        label: item.name,
                        value: item._id
                      };
                    }) ?? [];

                const facilityIdFromParam = searchParam.get('facilityId');
                const facilityIdExists = facilityDetails.some(
                  (option: any) => option.value === facilityIdFromParam
                );

                const initialFacilityValue = facilityIdExists
                  ? facilityIdFromParam
                  : undefined;

                return (
                  <Form.Item
                    name={AIDocumentManagerKey.FACILITY_TYPE}
                    label={AIDocumentManagerLabel.FACILITY_TYPE}
                    labelCol={{span: 24}}
                    rules={[
                      {
                        required: true,
                        message: CommonErrors.mandatory(
                          AIDocumentManagerLabel.FACILITY_TYPE
                        )
                      }
                    ]}
                    initialValue={initialFacilityValue}
                    validateFirst
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Facility Name"
                      defaultValue={undefined}
                      options={facilityDetails}
                      popupMatchSelectWidth={false}
                      size="middle"
                      filterOption={filterSelectOptions}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={AIDocumentManagerKey.DOC_TYPE}
              label={AIDocumentManagerLabel.BILL_TYPE}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(
                    AIDocumentManagerLabel.BILL_TYPE
                  )
                }
              ]}
              initialValue={undefined}
              validateFirst
            >
              <Select
                showSearch
                allowClear
                placeholder={AIDocumentManagerLabel.BILL_TYPE}
                defaultValue={undefined}
                options={CADocumentTypeOptions.filter((item) => !item?.hidden)}
                popupMatchSelectWidth={false}
                size="middle"
                onChange={() => {
                  FormInstance.setFieldValue(
                    AIDocumentManagerKey.BILL_TYPE,
                    undefined
                  );
                  FormInstance.setFieldValue(
                    AIDocumentManagerKey.METHOD,
                    undefined
                  );
                }}
                filterOption={filterSelectOptions}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              shouldUpdate={(prev, curr) =>
                prev[AIDocumentManagerKey.DOC_TYPE] !==
                curr[AIDocumentManagerKey.DOC_TYPE]
              }
              noStyle
            >
              {({getFieldValue}) => {
                const docType = getFieldValue(AIDocumentManagerKey.DOC_TYPE);

                const subDocTypes = CADocumentTypeOptions.filter(
                  (item) => item.value === docType && item?.children
                );

                return (
                  subDocTypes?.[0] && (
                    <Form.Item
                      name={AIDocumentManagerKey.BILL_TYPE}
                      label={AIDocumentManagerLabel.SUB_BILL_TYPE}
                      labelCol={{span: 24}}
                      rules={[
                        {
                          required: true,
                          message: CommonErrors.mandatory(
                            AIDocumentManagerLabel.BILL_TYPE
                          )
                        }
                      ]}
                      initialValue={undefined}
                      validateFirst
                    >
                      <Select
                        className="select"
                        placeholder={`Choose ${AIDocumentManagerLabel.SUB_BILL_TYPE}`}
                        options={subDocTypes?.[0].children?.filter(
                          (item) => !item?.hidden
                        )}
                        onChange={() => {
                          FormInstance.setFieldValue(
                            AIDocumentManagerKey.METHOD,
                            undefined
                          );
                        }}
                        disabled={!subDocTypes}
                      />
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              shouldUpdate={(prev, curr) =>
                prev[AIDocumentManagerKey.BILL_TYPE] !==
                curr[AIDocumentManagerKey.BILL_TYPE]
              }
              noStyle
            >
              {({getFieldValue}) => {
                const docType = getFieldValue(AIDocumentManagerKey.DOC_TYPE);
                const billType = getFieldValue(AIDocumentManagerKey.BILL_TYPE);

                const subDocTypes: any = CADocumentTypeOptions.filter(
                  (item) => item.value === docType && item?.children
                )?.[0]?.children;

                const methodTypes = subDocTypes?.filter(
                  (item: any) => item.value === billType
                )?.[0]?.children;

                return (
                  methodTypes?.[0] && (
                    <Form.Item
                      name={AIDocumentManagerKey.METHOD}
                      label={AIDocumentManagerLabel.METHOD}
                      labelCol={{span: 24}}
                      rules={[
                        {
                          required: true,
                          message: CommonErrors.mandatory(
                            AIDocumentManagerLabel.METHOD
                          )
                        }
                      ]}
                      initialValue={undefined}
                      validateFirst
                    >
                      <Select
                        className="select"
                        placeholder={`Choose ${AIDocumentManagerLabel.METHOD}`}
                        options={methodTypes ?? []}
                        disabled={!methodTypes}
                      />
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>
            <Form.Item name={'type'} initialValue={'CA'}></Form.Item>
          </Col>
        </>
      )}
      {currentStep === 1 && (
        <>
          <Col className="gutter-row">
            <Form.Item noStyle>
              <DocumentUploadPreview
                formInstance={FormInstance}
                validationMessage="Please upload bill"
              />
            </Form.Item>
            <Form.Item name="file_name" hidden />
            <Form.Item name="pollingStatus" hidden />
          </Col>
        </>
      )}
      {currentStep === 2 && <Col className="gutter-row"></Col>}
    </>
  );
};

export default CADocumentManagerForm;
